import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { RootState } from '../../../../ducks/store';
import { setSearchQuery } from '../../../../ducks/features/navPanelSlice';
import { getCourses } from '../../../../ducks/courses';
import { getTeams } from '../../../../ducks/teams';
import { getUsers } from '../../../../ducks/users';

import { highlightMatch } from '../../../../utils/text';

import NavPanelSkeleton from '../NavPanelSkeleton';

import './SearchPanel.scss';

interface IProps {
  visible: boolean;
  onClose: () => void;
}

const DEBOUNCE_MS = 200;

const SearchPanel: FC<IProps> = ({ visible, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const searchQuery = useSelector((state: RootState) => state.navPanel.searchQuery);
  const courses = useSelector((state: RootState) => state.courses.list);
  const teams = useSelector((state: RootState) => state.teams.list);
  const users = useSelector((state: RootState) => state.users.list);

  const [inputValue, setInputValue] = useState(searchQuery);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const fetchInitiatedRef = useRef(false);

  // Sync local input when Redux query changes externally (e.g. cleared on section switch)
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  const query = searchQuery.trim().toLowerCase();

  // Auto-focus when panel becomes visible
  useEffect(() => {
    if (visible) {
      // Slight delay to let panel animation complete
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  // Fetch data only once the user has typed a search query.
  // Uses Promise.all so the skeleton stays visible until all fetches resolve,
  // preventing results from trickling in group-by-group.
  useEffect(() => {
    if (!visible || !query) {
      fetchInitiatedRef.current = false;
      return;
    }
    if (fetchInitiatedRef.current) return;

    const needsCourses = !courses?.length;
    const needsTeams = !teams?.length;
    const needsUsers = !users?.length;

    if (!needsCourses && !needsTeams && !needsUsers) return;

    fetchInitiatedRef.current = true;
    setIsSearchLoading(true);

    const fetches: Promise<unknown>[] = [];
    if (needsCourses) fetches.push((dispatch as any)(getCourses()));
    if (needsTeams) fetches.push((dispatch as any)(getTeams()));
    if (needsUsers) fetches.push((dispatch as any)(getUsers()));

    Promise.all(fetches).finally(() => setIsSearchLoading(false));
  }, [visible, query, courses?.length, teams?.length, users?.length, dispatch]);

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        dispatch(setSearchQuery(value));
      }, DEBOUNCE_MS);
    },
    [dispatch],
  );

  // Cleanup-only: clear any pending debounce timer on unmount
  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const filteredCourses = useMemo(() => {
    if (!query || !courses) return [];
    return courses.filter((c) => c.title?.toLowerCase().includes(query));
  }, [query, courses]);

  const filteredTeams = useMemo(() => {
    if (!query || !teams) return [];
    return teams.filter((t) => t.name?.toLowerCase().includes(query));
  }, [query, teams]);

  const filteredUsers = useMemo(() => {
    if (!query || !users) return [];
    return users.filter((u) => {
      const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').toLowerCase();
      return fullName.includes(query);
    });
  }, [query, users]);

  const hasResults =
    filteredCourses.length > 0 || filteredTeams.length > 0 || filteredUsers.length > 0;
  const hasQuery = query.length > 0;

  const isDebouncing = inputValue.trim().toLowerCase() !== query;
  const showSkeleton =
    isSearchLoading || (isDebouncing && inputValue.trim().length > 0 && !hasResults);

  return (
    <div className="SearchPanel">
      <div className="SearchPanel-header">
        <span className="material-icons SearchPanel-header-icon">search</span>
        <input
          ref={inputRef}
          className="SearchPanel-header-input"
          type="text"
          placeholder="Search..."
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
        />
        <button
          className="SearchPanel-header-close"
          onClick={onClose}
          aria-label="Close search panel"
          type="button"
        >
          <span className="material-icons">close</span>
        </button>
      </div>

      <div className="SearchPanel-results NavPanel-list dark-scroll">
        {showSkeleton ? (
          <NavPanelSkeleton />
        ) : (
          <>
            {hasQuery && !hasResults ? (
              <div className="SearchPanel-empty">No results found</div>
            ) : null}

            {filteredCourses.length > 0 ? (
              <div className="SearchPanel-group">
                <div className="SearchPanel-group-header">Courses</div>
                {filteredCourses.map((course) => (
                  <div
                    key={course._id}
                    className="NavPanel-item SearchPanel-item"
                    role="button"
                    onClick={() => navigate(`/admin/courses/${course._id}`)}
                  >
                    <span className="NavPanel-item-text">
                      {highlightMatch(course.title || '', searchQuery)}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}

            {filteredTeams.length > 0 ? (
              <div className="SearchPanel-group">
                <div className="SearchPanel-group-header">Teams</div>
                {filteredTeams.map((team) => (
                  <div
                    key={team._id}
                    className="NavPanel-item SearchPanel-item"
                    role="button"
                    onClick={() => navigate(`/admin/teams/${team._id}`)}
                  >
                    <span className="NavPanel-item-text">
                      {highlightMatch(team.name || '', searchQuery)}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}

            {filteredUsers.length > 0 ? (
              <div className="SearchPanel-group">
                <div className="SearchPanel-group-header">Users</div>
                {filteredUsers.map((user) => {
                  const displayName =
                    [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

                  return (
                    <div
                      key={user._id}
                      className="NavPanel-item SearchPanel-item"
                      role="button"
                      onClick={() => navigate(`/admin/users?userId=${user._id}`)}
                    >
                      <span className="NavPanel-item-text">
                        {highlightMatch(displayName || '', searchQuery)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(SearchPanel);
